import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/cn';
import { useMediaQuery } from '@/lib/use-media-query';
import { DialogContent } from './dialog';
import {
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from './drawer';

/**
 * A responsive action panel: a **bottom sheet on phones**, a **centered modal on
 * desktop** (≥768px). Both are the same Radix Dialog primitive under the hood —
 * only the anchored `Content` + its enter animation differ — so this just picks
 * `DrawerContent` or `DialogContent` by viewport. Compound parts (`ModalHeader`
 * etc.) are shared; they read fine in both shapes.
 *
 * Use it for user-facing panels (compose, pick, report). Admin keeps `Sheet`.
 *
 * ```tsx
 * <Modal open={open} onOpenChange={setOpen}>
 *   <ModalContent>
 *     <ModalHeader><ModalTitle>…</ModalTitle></ModalHeader>
 *     …body…
 *   </ModalContent>
 * </Modal>
 * ```
 */
export const Modal = DialogPrimitive.Root;
export const ModalTrigger = DialogPrimitive.Trigger;
export const ModalClose = DialogPrimitive.Close;

export const ModalContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, ...props }, ref) => {
  const desktop = useMediaQuery('(min-width: 768px)');
  return desktop ? (
    <DialogContent
      ref={ref}
      className={cn('max-h-[85dvh] overflow-y-auto', className)}
      {...props}
    />
  ) : (
    <DrawerContent ref={ref} className={className} {...props} />
  );
});
ModalContent.displayName = 'ModalContent';

export {
  DrawerHeader as ModalHeader,
  DrawerFooter as ModalFooter,
  DrawerTitle as ModalTitle,
  DrawerDescription as ModalDescription,
};
